import type {
  CourseDetailResponse,
  SessionResponse,
  SurveyResponse,
} from '@lms/api-client/backend';
import { LoaderCircleIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocale } from 'use-intl';
import { ScormAPI2004 } from '../scorm.js';
import { ScormAPI } from '../scorm12.js';
import AfterSurvey from './after-survey';
import BeforeSurvey from './before-survey';
import StateBanner from './state-banner';

declare global {
  interface Window {
    API: any;
    API_1484_11: any;
  }
}

if (window) {
  if (window.API_1484_11 === undefined) {
    window.API_1484_11 = new ScormAPI2004();
  }
  if (window.API === undefined) {
    window.API = new ScormAPI();
  }
}

export default function Player({
  session,
  course,
  afterSurvey,
  beforeSurvey,
}: {
  session?: SessionResponse;
  course: CourseDetailResponse;
  afterSurvey?: SurveyResponse;
  beforeSurvey?: SurveyResponse;
}) {
  const locale = useLocale();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (iframeRef.current !== null && !loaded) {
      // On frame reload, we must first reset API.
      window.API_1484_11.reset(false);
      //window.API_1484_11.apiLogLevel = 1;
      window.API_1484_11.on('Initialize', () => setLoaded(true));

      if (session?.scormData) {
        try {
          window.API_1484_11.loadFromJSON(session.scormData ?? {});
        } catch (e) {
          console.error(
            'Failed to set existing scormData, continuing as if blank',
            e,
          );
        }
      }

      const targetUrl =
        course.data[locale]?.scorm ??
        course.data.en?.scorm ??
        course.data[0]?.scorm ??
        '/404';

      // Only set it if it's not already set, otherwise we trigger reloads and re-inits etc.
      if (iframeRef.current.src !== targetUrl) {
        iframeRef.current.src = targetUrl;
      }
    }
  }, [session, course.data, locale, loaded]);

  return (
    <>
      <div className="relative h-dvh w-dvw" data-testid="course-player">
        <iframe
          ref={iframeRef}
          name="course"
          title="course"
          width="100%"
          height="100%"
          allowFullScreen={true}
          data-testid="scorm-iframe"
        />
        {!loaded && (
          <div
            className="absolute top-0 right-0 bottom-0 left-0 flex flex-col items-center justify-center bg-white"
            data-testid="course-loading"
          >
            <LoaderCircleIcon className="animate-spin" />
          </div>
        )}
      </div>
      <StateBanner session={session} />
      {afterSurvey && <AfterSurvey session={session} survey={afterSurvey} />}
      {beforeSurvey && <BeforeSurvey session={session} survey={beforeSurvey} />}
    </>
  );
}
